import { AuthServices } from '@/features/auth/services';
import { auth } from '@/features/auth/types';

import type { GuardianProfile } from './get-guardian';

/**
 * Welcome / "Get Started" guide progress, backed by `/api/v1/guardians/me/getting-started`
 * (POST to start, PATCH to mark a step). The progress itself is read from the guardian profile's
 * `gettingStarted` block (GET `/guardians/me`). Every call returns the refreshed profile so the
 * caller can seed it into the profile cache.
 */

/** Get Started step numbers (also the `step` sent to the API). */
export const WELCOME_STEPS = {
  ADD_PLAYER: 1,
  BOOK_LESSON: 2,
  TRACK_PROGRESS: 3,
} as const;

/** Background calls — don't surface the shared error popup (e.g. a 409 when re-marking a step). */
const SILENT = { skipServerErrorAlert: true } as const;

/** Start the guide (idempotent). */
export async function startWelcomeProgress(): Promise<GuardianProfile> {
  return await AuthServices.startGuardianGettingStarted(SILENT);
}

/** Mark a Get Started step (1..3) done. */
export async function saveWelcomeProgress(step: number): Promise<GuardianProfile> {
  return await AuthServices.updateGuardianGettingStarted({ step }, SILENT);
}

/** Dismiss the guide (close it early without finishing). */
export async function dismissWelcomeProgress(): Promise<GuardianProfile> {
  return await AuthServices.dismissGuardianGettingStarted(SILENT);
}

/**
 * Highest completed Get Started step from the profile's `gettingStarted` block. `completed`
 * status → all steps done; otherwise the max step number present in `steps` (each keyed step is done).
 */
export function getWelcomeCompletedStep(gettingStarted?: auth.GettingStarted): number {
  if (!gettingStarted) return 0;
  if (gettingStarted.status === 'completed') return WELCOME_STEPS.TRACK_PROGRESS;
  const doneSteps = Object.keys(gettingStarted.steps ?? {})
    .map(Number)
    .filter(step => Number.isFinite(step));
  return doneSteps.length ? Math.max(...doneSteps) : 0;
}
